import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { movies, genreOptions } from "@/mocks/movies";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";

export default function Home() {
  const featured = movies[0];
  const [showDemoTip, setShowDemoTip] = useState(false);

  const latest = useMemo(
    () => [...movies].sort((a, b) => b.year - a.year).slice(1, 9),
    []
  );
  const topRated = useMemo(
    () => [...movies].sort((a, b) => b.rating - a.rating).slice(0, 5),
    []
  );

  const collections = [
    { title: "婚姻与信任", desc: "当亲密关系遭遇考验，信任还能剩下多少", tag: "婚姻" },
    { title: "悬疑与反转", desc: "层层剥开的真相，直到最后一刻才看清", tag: "悬疑" },
    { title: "记忆与往事", desc: "被时间封存的故事，终有被翻开的一天", tag: "回忆" },
  ];

  return (
    <div className="bg-background-950">
      {/* Hero */}
      <section className="relative min-h-[560px] md:min-h-[680px] flex items-center overflow-hidden">
        <img
          src={featured.backdrop}
          alt={`${featured.title} 剧照`}
          title={`${featured.title} 电影剧照`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/85 to-background-950/30"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/60"></div>

        <div className="relative mx-auto max-w-site w-full px-4 md:px-6 pt-28 pb-16">
          <div className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500/15 border border-primary-500/40 text-primary-200 text-xs font-label tracking-wide">
              <i className="ri-star-fill text-accent-400"></i>
              焦点影片 · {featured.rating.toFixed(1)} 分
            </span>
            <h1 className="mt-5 font-heading text-4xl md:text-6xl font-bold text-foreground-50 leading-tight">
              {featured.title}
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-400 font-label tracking-wide">
              {featured.originalTitle} · {featured.year} · {featured.region} · {featured.duration}分钟
            </p>
            <p className="mt-5 text-sm md:text-base text-foreground-200 leading-relaxed max-w-xl">
              {featured.synopsis}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              {featured.genres.map((g) => (
                <span key={g} className="px-3 py-1 rounded-full bg-background-800/80 text-foreground-200 text-xs">
                  {g}
                </span>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-foreground-400">
              <span className="flex items-center gap-1.5">
                <i className="ri-movie-2-line text-accent-400"></i> 导演 {featured.director}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-star-line text-accent-400"></i>
                {featured.cast
                  .slice(0, 3)
                  .map((c) => c.name)
                  .join(" · ")}
              </span>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${featured.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line"></i> 查看剧情详情
              </Link>
              <button
                type="button"
                onClick={() => setShowDemoTip(true)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-600 text-foreground-100 text-sm font-medium hover:border-accent-500 hover:text-accent-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line"></i> 播放预告（演示）
              </button>
            </div>
            {showDemoTip && (
              <p className="mt-3 text-xs text-accent-400/90 animate-fade-in">
                本站为影视资讯演示站点，暂不提供在线播放功能，敬请期待。
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Category shortcuts */}
      <section className="mx-auto max-w-site px-4 md:px-6 py-10 md:py-14">
        <SectionHeading title="分类快捷入口" subtitle="按题材快速进入感兴趣的影视内容" to="/category" linkLabel="全部电影" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {genreOptions.map((g) => {
            const count = movies.filter((m) => m.genres.includes(g)).length;
            return (
              <Link
                key={g}
                to={`/category?genre=${encodeURIComponent(g)}`}
                className="group rounded-lg bg-background-900/70 border border-background-800 p-4 hover:border-primary-700 transition-colors"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-background-800 text-accent-400 text-xl group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                  <i className="ri-film-line"></i>
                </span>
                <p className="mt-3 font-heading text-sm text-foreground-100">{g}</p>
                <p className="text-xs text-foreground-500 mt-1">{count} 部作品</p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Latest + sidebar */}
      <section className="mx-auto max-w-site px-4 md:px-6 pb-12 md:pb-20">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
          {/* main */}
          <div className="flex-1 min-w-0">
            <SectionHeading title="最新资讯 / 内容" subtitle="近期更新的相关影视内容" to="/category" linkLabel="查看更多" />
            {latest.length === 0 ? (
              <div className="rounded-lg border border-dashed border-background-700 p-10 text-center text-foreground-500 text-sm">
                暂无最新内容，稍后再来看看吧。
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                {latest.map((m) => (
                  <MovieCard key={m.id} movie={m} variant="compact" />
                ))}
              </div>
            )}
          </div>

          {/* sidebar */}
          <aside className="w-full lg:w-[320px] shrink-0 space-y-8">
            <div>
              <SectionHeading title="推荐专题合集" />
              <div className="space-y-3">
                {collections.map((c) => (
                  <Link
                    key={c.title}
                    to="/recommendations"
                    className="group block rounded-lg bg-background-900/70 border border-background-800 p-4 hover:border-primary-700 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-heading text-sm text-foreground-100 group-hover:text-accent-400 transition-colors">
                        {c.title}
                      </span>
                      <i className="ri-arrow-right-up-line text-foreground-500 group-hover:text-accent-400 transition-colors"></i>
                    </div>
                    <p className="text-xs text-foreground-500 mt-2 leading-relaxed">{c.desc}</p>
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <SectionHeading title="高分推荐" />
              <div className="rounded-lg bg-background-900/70 border border-background-800 divide-y divide-background-800">
                {topRated.map((m, i) => (
                  <Link
                    key={m.id}
                    to={`/detail/${m.id}`}
                    className="flex items-center gap-3 p-3 hover:bg-background-800/60 transition-colors"
                  >
                    <span className="w-6 text-center font-label text-sm text-accent-400">{i + 1}</span>
                    <div className="w-11 h-16 shrink-0 rounded-md overflow-hidden bg-background-800">
                      <img
                        src={m.poster}
                        alt={`${m.title} 海报`}
                        title={`${m.title} 影视内容`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-foreground-100 line-clamp-1">{m.title}</p>
                      <p className="text-xs text-foreground-500 mt-1">
                        {m.year} · {m.region}
                      </p>
                    </div>
                    <span className="ml-auto font-label text-sm text-foreground-300">{m.rating.toFixed(1)}</span>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Site intro */}
      <section className="bg-background-900 border-y border-background-800">
        <div className="mx-auto max-w-site px-4 md:px-6 py-12 md:py-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-1">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-50">
              <Link to="/about" className="hover:text-accent-400 transition-colors">关于本站</Link>
            </h2>
            <p className="text-sm text-foreground-400 mt-3 leading-relaxed">
              我们围绕电影《交换朋友的妻子》及同类情感悬疑题材，整理剧情介绍、演员资料与影片推荐，帮助你快速了解一部影片的全貌。
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-1.5 mt-5 text-sm text-accent-400 hover:text-accent-300 transition-colors"
            >
              了解更多 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: "ri-film-fill", num: `${movies.length}+`, label: "虚构影片资料" },
              { icon: "ri-user-voice-line", num: "80+", label: "演员资料条目" },
              { icon: "ri-compass-3-line", num: "3", label: "主题推荐专题" },
            ].map((s) => (
              <div key={s.label} className="rounded-lg bg-background-950/60 border border-background-800 p-5">
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-500/15 text-accent-400 text-xl">
                  <i className={s.icon}></i>
                </span>
                <p className="mt-3 font-label text-2xl text-foreground-50">{s.num}</p>
                <p className="text-xs text-foreground-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}